import * as babylon from "@babylonjs/core";

import { CAMERA_TYPE } from "@/shared/state/visualization/schema/v1/three-d";

import type { BabylonState, Vector3Plain } from "../../state";

import { attachLatchedDrag } from "./attachLatchedDrag";

/**
 * Floor on `cos(β)` in the foreshortening divisor. Sets the pan
 * sensitivity ceiling: `1 / MIN_COS_BETA` is the maximum amplification
 * applied to `basePanScale` at any tilt. With `MIN_COS_BETA = 1/3`,
 * the divisor caps at `3×` — the user-facing pan rate stays
 * comfortably below the `1 / sin(10°) ≈ 5.7×` cursor-stable maximum
 * that the old MIN value targeted.
 */
export const MIN_COS_BETA = 1 / 3;

/**
 * Ceiling on `cos(β)` in the foreshortening divisor. Set equal to
 * `MIN_COS_BETA` so the divisor is constant across all tilts: pan
 * rate is orientation-independent. Without this clamp, pan speed
 * would collapse at `β = 0` (looking straight down) because no
 * foreshortening correction is needed when the image plane is
 * parallel to the grid — leaving the rate at `basePanScale * PAN_SENS`,
 * which felt noticeably weaker than every other orientation. Equal
 * MIN/MAX trades the cursor-stable invariant for uniform feel.
 */
export const MAX_COS_BETA = MIN_COS_BETA;

/**
 * Multiplier applied to the cursor-stable `unitsPerPixel` rate. The
 * math-perfect rate (factor = 1) keeps the world point under the
 * cursor exactly under the cursor across a drag — natural for "grab
 * the world and move it" gameplay UX. For data viewing, that rate
 * tends to feel over-reactive; this damping (< 1) gives drags more
 * "weight": the world slides at 50% of cursor speed, so the spot you
 * grabbed lags the cursor as you drag.
 */
export const PAN_SENS = 0.5;

/**
 * Compute the world-space focus-point delta produced by a pixel-space
 * pointer drag, in yaw-aligned ground-projected form.
 *
 * Math:
 *   right = (-sin α, 0,  cos α)              // camera's local +x in world XZ
 *   fwd   = (-cos α, 0, -sin α)              // camera's local +z (forward) in world XZ
 *   unitsPerPixel = PAN_SENS · (2 · radius · tan(fov/2)) / renderHeight
 *                   / clamp(cos β, MIN_COS_BETA, MAX_COS_BETA)
 *   Δfp = right · (-dx · upp) + fwd · (dy · upp)
 *
 * The `(2 · radius · tan(fov/2)) / renderHeight` factor is the world-
 * units height of one screen pixel at the focus distance (image-plane
 * height divided by render-target height). The `1 / clamp(cos β, ...)`
 * factor would compensate for screen foreshortening at steep tilts to
 * keep the world point under the cursor stable; clamping `cos β`
 * between `MIN_COS_BETA` and `MAX_COS_BETA` instead pins the rate at
 * a constant value across all tilts, in exchange for abandoning the
 * cursor-stable invariant. `PAN_SENS` damps below the clamped rate so
 * drags feel more controlled for data viewing.
 *
 * Modeling shortcut: the foreshortening factor is applied to BOTH
 * `right` and `fwd` axes, but only the forward axis is foreshortened
 * by `cos β` on the ground (the right axis stays in-plane regardless
 * of tilt). Applying the factor uniformly overshoots cursor-stability
 * on horizontal drags at steep tilts. Acceptable in practice because
 * the magnitude error is small at the tilts users typically operate
 * at; revisit if cursor-stability complaints surface.
 *
 * Y component of Δfp is zero — pan slides along the world XZ plane,
 * not along the camera's view frustum.
 */
export function computePanDelta(
  babylonCamera: babylon.ArcRotateCamera,
  renderHeight: number,
  dx: number,
  dy: number,
): Vector3Plain {
  const alpha = babylonCamera.alpha;
  const beta = babylonCamera.beta;
  const radius = babylonCamera.radius;
  const fov = babylonCamera.fov;

  const basePanScale = (2 * radius * Math.tan(fov / 2)) / renderHeight;
  const cosBeta = Math.cos(beta);
  const cosClamp = Math.min(
    Math.max(Math.abs(cosBeta), MIN_COS_BETA),
    MAX_COS_BETA,
  );
  const unitsPerPixel = (basePanScale / cosClamp) * PAN_SENS;

  // When the camera crosses β = π/2 (below the grid plane), Babylon's
  // ArcRotateCamera flips its screen-up vector with respect to world
  // XZ — the projection of screen-up onto the ground reverses sign.
  // The right axis is unaffected (cross(view, worldUp) keeps the same
  // XZ orientation across the pole), so we flip only `fwd`. Without
  // this, drag-down below the grid would pan opposite the cursor-
  // stable direction.
  const fwdSign = cosBeta >= 0 ? 1 : -1;

  const rightX = -Math.sin(alpha);
  const rightZ = Math.cos(alpha);
  const fwdX = -Math.cos(alpha) * fwdSign;
  const fwdZ = -Math.sin(alpha) * fwdSign;

  return {
    x: (rightX * -dx + fwdX * dy) * unitsPerPixel,
    y: 0,
    z: (rightZ * -dx + fwdZ * dy) * unitsPerPixel,
  };
}

/**
 * Add a pan delta to the input accumulator consumed by `updateCamera`.
 * Reads the active third-person camera's pose to compute the delta in
 * world XZ, then sums it into `state.input.cameraDeltas.panX/panZ`.
 * `updateCamera`'s third-person branch applies the accumulator to
 * `camera.focusPoint.x/z` and zeroes it each frame.
 */
export function applyPanDelta(
  state: BabylonState,
  dx: number,
  dy: number,
): void {
  const camera = state.sceneConfig.camera;
  if (!camera || camera.spec.type !== CAMERA_TYPE.thirdPerson) {
    return;
  }
  const renderHeight = state.context.engine.getRenderHeight();
  const delta = computePanDelta(camera.babylonCamera, renderHeight, dx, dy);
  state.input.cameraDeltas.panX += delta.x;
  state.input.cameraDeltas.panZ += delta.z;
}

/**
 * Pointer-down predicate for the pan gesture. Latches on shift +
 * left-click when a third-person camera is active. Releasing shift
 * mid-drag does not abort the gesture; the latch holds until the
 * pointer lifts.
 */
export function shouldLatchPan(state: BabylonState, e: PointerEvent): boolean {
  if (e.button !== 0) {
    return false;
  }
  if (!e.shiftKey) {
    return false;
  }
  return state.sceneConfig.camera?.spec.type === CAMERA_TYPE.thirdPerson;
}

/**
 * Attach a shift+left-drag pan handler to the BabylonManager's
 * canvas. Returns a cleanup that detaches listeners and releases any
 * in-flight pointer capture.
 *
 * No-op for orbit cameras: `shouldLatchPan` returns false, so the
 * pointerdown event is not consumed and falls through to Babylon's
 * default pan input (still wired on the orbit branch). We
 * deliberately do not call `preventDefault` / `stopPropagation` to
 * preserve that fall-through.
 */
export function attachPanInput(state: BabylonState): () => void {
  return attachLatchedDrag({
    canvas: state.context.canvas,
    shouldLatch: (e) => shouldLatchPan(state, e),
    onMove: (_e, dx, dy) => applyPanDelta(state, dx, dy),
  });
}
